import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useQuery, useLazyQuery } from '@apollo/client'
import { useDispatch } from 'react-redux'
import { Box, CircularProgress } from '@material-ui/core'
import ImageLayer from 'ol/layer/Image'
import ImageWMS from 'ol/source/ImageWMS'
import toast from 'react-hot-toast'
import { useTranslation } from '@flint/locales'
import { toggleFormDrawer } from 'store/layout'
import { Layout, ORGANIZATION_ID, WMS_CLICK_INTERACTION_NAME } from 'shared'
import { ResponsiveFormContainer } from 'components/AppDrawers/ResponsiveFormContainer'
import BaseMap from 'pages/Map/BaseMap'
import { RasedMap } from 'pages/Map/RasedMap'
import { createWmsLayer } from 'pages/Map/createWmsLayer'
import { WMSClick } from 'pages/Map/OnClickInteraction'
import { getPolygonExtent } from 'utils/map.utils'
import { GET_QA_ORDERS, GET_QA_RECORD } from 'global/graphql/SimpleProjectQA'
import { LayersControlPanel } from 'components/LayersControlPanel'
import { QARecordPanel } from './QARecordPanel'
import { QARecordSearch } from './QARecordSearch'

const QA_WMS_URL = process.env.REACT_APP_WMS_URL_SIMPLE_QA

export const QAOrderMap = () => {
  const { projectId, orderCode } = useParams<{
    projectId: string
    orderCode: string
  }>()
  const { state } = useLocation() as { state: { order?: any } | null }
  const mapRef = useRef<RasedMap | null>(null)
  const paLayerRef = useRef<ImageLayer<ImageWMS> | null>(null)
  const dataLayerRef = useRef<ImageLayer<ImageWMS> | null>(null)
  const wmsClickRef = useRef<any>(null)

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isClickLoading, setIsClickLoading] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [searchRecord, setSearchRecord] = useState<any>(null)

  const openPanel = () => {
    setPanelOpen(true)
    dispatch(toggleFormDrawer(true))
  }

  const closePanel = () => {
    setPanelOpen(false)
    setSearchRecord(null)
    dispatch(toggleFormDrawer(false))
  }

  const handleRecordFound = (found: any) => {
    setSearchRecord(found)
    openPanel()
  }

  // Close the Redux drawer on unmount so it doesn't leak into other pages
  useEffect(() => {
    return () => {
      dispatch(toggleFormDrawer(false))
    }
  }, [])

  const { data: ordersData, loading: ordersLoading } = useQuery(GET_QA_ORDERS, {
    variables: { projectId: projectId ? parseInt(projectId, 10) : 0 },
    skip: !projectId,
  })

  // Prefer fresh query result (has boundaries); fall back to navigation state
  // while the query is in-flight so the map doesn't wait on a cold load.
  const order = useMemo(
    () =>
      ordersData?.orders?.data?.find((o: any) => o.code === orderCode) ??
      state?.order,
    [state?.order?.id, ordersData, orderCode]
  )

  const [
    fetchRecord,
    { data: recordData, loading: recordLoading, refetch: refetchRecord },
  ] = useLazyQuery(GET_QA_RECORD)

  const record = searchRecord ?? recordData?.qaRecords?.data?.[0]

  useEffect(() => {
    if (!order || !mapRef.current) return

    const paSource = createWmsLayer({
      wmsLayerName: order.paLayer.wmsFeatureTypeName,
      url: QA_WMS_URL,
    })

    const dataSource = createWmsLayer({
      wmsLayerName: order.dataLayer.wmsFeatureTypeName,
      url: QA_WMS_URL,
    })

    if (paSource) {
      const paLayer = new ImageLayer({ source: paSource })
      paLayer.set('toggleable', true)
      paLayer.set('id', 'qa-pa-layer')
      paLayer.set('name', order.paLayer.wmsFeatureTypeName)
      paLayer.set('label', 'منطقة التوجيه')
      paLayerRef.current = paLayer
      mapRef.current.addLayer(paLayer)
    }

    if (dataSource) {
      const dataLayer = new ImageLayer({ source: dataSource })
      dataLayer.set('toggleable', true)
      dataLayer.set('id', 'qa-data-layer')
      dataLayer.set('name', order.dataLayer.wmsFeatureTypeName)
      dataLayer.set('label', 'قطع الأراضي/ النقاط')
      dataLayerRef.current = dataLayer
      mapRef.current.addLayer(dataLayer)

      const wmsClick = new WMSClick(
        false,
        dataSource,
        ORGANIZATION_ID,
        () => setIsClickLoading(true),
        (data: any) => {
          setIsClickLoading(false)
          const features = data?.features
          if (!features?.length) {
            toast.error(t('no-record-found'), { duration: 1500 })
            return
          }
          const featureId = features[0]?.properties?.id
          if (!featureId) return
          fetchRecord({ variables: { orderId: order.id, pk: featureId } })
          openPanel()
        },
        (err: Error) => {
          setIsClickLoading(false)
          if (err?.name !== 'AbortError') {
            toast.error(t('failed alert title'), { duration: 1500 })
          }
        }
      )
      wmsClick.set('name', WMS_CLICK_INTERACTION_NAME)
      wmsClickRef.current = wmsClick
      mapRef.current.addInteraction(wmsClick)
    }

    const boundaries =
      order.dataLayer?.boundaries ?? order.paLayer?.boundaries ?? null
    if (boundaries && mapRef.current) {
      try {
        const extent = getPolygonExtent(boundaries)
        mapRef.current.getView().fit(extent, { duration: 1000, maxZoom: 14 })
      } catch {
        // ignore malformed boundaries
      }
    }

    return () => {
      if (mapRef.current) {
        if (wmsClickRef.current) {
          mapRef.current.removeInteraction(wmsClickRef.current)
          wmsClickRef.current = null
        }
        if (dataLayerRef.current) {
          mapRef.current.removeLayer(dataLayerRef.current)
          dataLayerRef.current = null
        }
        if (paLayerRef.current) {
          mapRef.current.removeLayer(paLayerRef.current)
          paLayerRef.current = null
        }
      }
      setPanelOpen(false)
    }
  }, [order?.id])

  return (
    <Layout>
      <Box position="relative" height="calc(100vh - 55px)">
        <BaseMap mapRef={mapRef} />

        {((ordersLoading && !order) || isClickLoading || recordLoading) && (
          <Box
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1001,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {order && (
          <Box
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <LayersControlPanel map={mapRef.current} />
            <QARecordSearch
              mapRef={mapRef}
              order={order}
              onRecordFound={handleRecordFound}
            />
          </Box>
        )}

        <ResponsiveFormContainer
          open={panelOpen && !!record && !recordLoading}
          onClose={closePanel}
          form={
            record && !recordLoading ? (
              <QARecordPanel
                record={record}
                orderId={order.id}
                jsonSchema={order.jsonSchema}
                onClose={closePanel}
                onRecordUpdated={() => {
                  if (searchRecord) {
                    closePanel()
                  } else {
                    refetchRecord()
                  }
                }}
              />
            ) : null
          }
        />
      </Box>
    </Layout>
  )
}
